import React from "react";
import { Document, Image, Page, StyleSheet, Text, View, pdf } from "@react-pdf/renderer";
import {
    applyPredesignSizeAttrs,
    formatPrintSizeCm,
    getSelectionEffectiveSizeCm,
    getSelectionSizeLabel,
    hasRealPrintContent,
    isFixedPredesignSelection,
    resolvePrintPreviewUrl,
} from "../../utils/designerHelpers";
import {
    MASTER_PRINT_FONT_BASE_URL,
    MASTER_PART_TYPE,
    getFontById,
    getFontFaceUrl,
    sanitizeColorName,
} from "../../utils/designerUtils";
import littleBlueLogo from "../../images/logoAzul.png";

const formatCustomCutColorSummary = (color = null) => {
    const code = String(color?.code || "").trim();
    const name = String(color?.name || "").trim();
    if (name && code) return `${name} (${code})`;
    return name || code || "Sin color";
};

const styles = StyleSheet.create({
    page: {
        padding: 24,
        fontSize: 10,
        color: "#1f2d4d",
        fontFamily: "Helvetica",
    },
    header: {
        alignItems: "center",
        marginBottom: 14,
    },
    headerLogo: {
        width: 130,
        objectFit: "contain",
        marginBottom: 10,
    },
    title: {
        fontSize: 20,
        fontWeight: 700,
        letterSpacing: 1,
        textAlign: "center",
        marginBottom: 4,
    },
    subtitle: {
        fontSize: 10,
        color: "#5a6a85",
        textAlign: "center",
        marginBottom: 12,
    },
    block: {
        marginBottom: 12,
        border: "1 solid #dbe7fb",
        borderRadius: 6,
        padding: 10,
    },
    blockTitle: {
        fontSize: 12,
        fontWeight: 700,
        marginBottom: 8,
    },
    sectionTitle: {
        fontSize: 11,
        fontWeight: 700,
        marginTop: 4,
        marginBottom: 4,
    },
    row: {
        marginBottom: 3,
    },
    treeRow: {
        marginBottom: 1,
        flexDirection: "row",
        alignItems: "center",
    },
    treeGutter: {
        flexDirection: "row",
        alignItems: "stretch",
        marginRight: 4,
        minHeight: 18,
    },
    treeLevelCell: {
        width: 14,
        height: 18,
        position: "relative",
    },
    treeLevelLine: {
        position: "absolute",
        top: 0,
        bottom: 0,
        left: 7,
        width: 1,
        backgroundColor: "#646464",
    },
    treeConnectorCell: {
        width: 14,
        height: 18,
        position: "relative",
    },
    treeConnectorVertical: {
        position: "absolute",
        top: 0,
        bottom: 0,
        left: 7,
        width: 1,
        backgroundColor: "#646464",
    },
    treeConnectorVerticalLast: {
        position: "absolute",
        top: 0,
        height: 9,
        left: 7,
        width: 1,
        backgroundColor: "#646464",
    },
    treeConnectorHorizontal: {
        position: "absolute",
        top: 9,
        left: 7,
        width: 9,
        height: 1,
        backgroundColor: "#646464",
    },
    treeContent: {
        flexDirection: "row",
        alignItems: "center",
        flexWrap: "nowrap",
    },
    treeLabel: {
        fontSize: 10,
        color: "#1f2d4d",
    },
    treeLabelStrong: {
        fontSize: 10,
        color: "#1f2d4d",
        fontWeight: 700,
    },
    treeColorSwatch: {
        width: 11,
        height: 11,
        marginLeft: 8,
        marginRight: 5,
        border: "1 solid #111111",
    },
    treeColorText: {
        fontSize: 10,
        color: "#2f2f2f",
    },
    printRow: {
        width: "100%",
        alignItems: "flex-start",
        marginBottom: 14,
    },
    printPreview: {
        width: 230,
        height: 230,
        objectFit: "contain",
        borderRadius: 8,
        border: "1 solid #cfdcf3",
    },
    textPreview: {
        width: 230,
        height: 84,
        objectFit: "cover",
        borderRadius: 8,
        border: "1 solid #cfdcf3",
    },
    printMeta: {
        marginTop: 8,
    },
    printMetaRow: {
        marginBottom: 2,
    },
    printMetaTitle: {
        fontWeight: 700,
    },
    sketchWrap: {
        marginTop: 4,
        border: "1 solid #dbe7fb",
        borderRadius: 8,
        padding: 8,
    },
    sketchImage: {
        width: "100%",
        objectFit: "contain",
    },
});

const sanitizeColorLabel = (value) => {
    return sanitizeColorName(value, "");
};

const GuideDocument = ({
    orderItemId,
    garmentName = "Tu prenda",
    generatedAt,
    appliedPartsBySection = [],
    appliedPartsTreeRows = [],
    printResources = [],
    stripeStrokeSummary = null,
    customCutSummary = null,
    sketchDataUrl = null,
}) => (
    <Document>
        <Page size="A4" style={styles.page}>
            <View style={styles.header}>
                <Image src={littleBlueLogo} style={styles.headerLogo} />
                <Text style={styles.title}>RESUMEN DISEÑO</Text>
                <Text style={styles.subtitle}>
                    {`${garmentName || "Tu prenda"} · ${generatedAt}`}
                </Text>
            </View>

            <View style={styles.block}>
                <Text style={styles.blockTitle}>Partes aplicadas</Text>
                {appliedPartsTreeRows.length > 0 ? (
                    appliedPartsTreeRows.map((row, idx) => (
                        <View key={`tree-line-${row?.key || idx}`} style={styles.treeRow}>
                            <View style={styles.treeGutter}>
                                {(row?.ancestorHasNext || []).map((hasNext, ancestorIndex) => (
                                    <View key={`tree-prefix-${row?.key || idx}-${ancestorIndex}`} style={styles.treeLevelCell}>
                                        {hasNext ? <View style={styles.treeLevelLine} /> : null}
                                    </View>
                                ))}
                                <View style={styles.treeConnectorCell}>
                                    <View style={row?.isLast ? styles.treeConnectorVerticalLast : styles.treeConnectorVertical} />
                                    <View style={styles.treeConnectorHorizontal} />
                                </View>
                            </View>
                            <View style={styles.treeContent}>
                                <Text style={row?.isPartType ? styles.treeLabelStrong : styles.treeLabel}>
                                    {row?.label || "Parte"}
                                </Text>
                                {row?.colorLabel ? (
                                    <View style={styles.treeContent}>
                                        <View style={[
                                            styles.treeColorSwatch,
                                            row?.colorCode ? { backgroundColor: row.colorCode } : null,
                                        ]} />
                                        <Text style={styles.treeColorText}>{row.colorLabel}</Text>
                                    </View>
                                ) : null}
                            </View>
                        </View>
                    ))
                ) : appliedPartsBySection.length === 0 ? (
                    <Text style={styles.row}>Sin partes aplicadas.</Text>
                ) : appliedPartsBySection.map((group) => (
                    <View key={`parts-${group.section || "base"}`}>
                        <Text style={styles.sectionTitle}>{group.section || "Base"}</Text>
                        {(group.items || []).map((item) => {
                            const color = sanitizeColorLabel(item?.colorName || "");
                            return (
                                <Text key={`part-${group.section}-${item.key}`} style={styles.row}>
                                    {`• ${item?.label || "Parte"}${color ? ` — ${color}` : ""}`}
                                </Text>
                            );
                        })}
                    </View>
                ))}
            </View>

            <View style={styles.block}>
                <Text style={styles.blockTitle}>Diseños aplicados</Text>
                {printResources.length === 0 ? (
                    <Text style={styles.row}>Sin diseños aplicados.</Text>
                ) : printResources.map((resource, idx) => (
                    <View key={`resource-${idx}`} style={styles.printRow}>
                        {resource.previewUrl ? (
                            <Image
                                src={resource.previewUrl}
                                style={resource.kind === "text" ? styles.textPreview : styles.printPreview}
                            />
                        ) : (
                            <Text style={styles.row}>Sin vista previa disponible.</Text>
                        )}
                        <View style={styles.printMeta}>
                            <Text style={styles.printMetaRow}>
                                <Text style={styles.printMetaTitle}>Tipo:</Text>
                                {` ${resource.kindLabel}`}
                            </Text>
                            <Text style={styles.printMetaRow}>
                                <Text style={styles.printMetaTitle}>Técnica:</Text>
                                {` ${resource.techniqueLabel || "Bordado"}`}
                            </Text>
                            <Text style={styles.printMetaRow}>
                                <Text style={styles.printMetaTitle}>Tamaño:</Text>
                                {` ${resource.sizeLabel || "Sin tamaño"}`}
                            </Text>
                            {resource.kind === "text" && resource.fontLabel ? (
                                <Text style={styles.printMetaRow}>
                                    <Text style={styles.printMetaTitle}>Tipografía:</Text>
                                    {` ${resource.fontLabel}`}
                                </Text>
                            ) : null}
                            {resource.kind === "text" && resource.textValue ? (
                                <Text style={styles.printMetaRow}>
                                    <Text style={styles.printMetaTitle}>Texto:</Text>
                                    {` ${resource.textValue}`}
                                </Text>
                            ) : null}
                            <Text style={styles.printMetaRow}>
                                <Text style={styles.printMetaTitle}>Colores:</Text>
                                {` ${resource.appliedColorsLabel || "Sin colores aplicados"}`}
                            </Text>
                        </View>
                    </View>
                ))}
            </View>
            <View style={styles.block}>
                <Text style={styles.blockTitle}>Tiras aplicadas</Text>
                <Text style={styles.row}>
                    {`Total: ${Number(stripeStrokeSummary?.total || 0)} trazos · Rectas: ${Number(stripeStrokeSummary?.straight || 0)} · Serpentinas: ${Number(stripeStrokeSummary?.curved || 0)}`}
                </Text>
            </View>
            <View style={styles.block}>
                <Text style={styles.blockTitle}>Recortes personalizados</Text>
                {(
                    Array.isArray(customCutSummary?.details) && customCutSummary.details.length > 0
                        ? customCutSummary.details
                        : Array.from({ length: Number(customCutSummary?.total || 0) }, (_, index) => ({
                            id: `custom-cut-fallback-${index + 1}`,
                            label: `Recorte ${index + 1}`,
                            colors: [],
                        }))
                ).map((detail) => (
                    <Text key={`custom-cut-${detail.id}`} style={styles.row}>
                        {`${detail.label}: ${((detail.colors || []).length > 0
                            ? detail.colors.map((color) => formatCustomCutColorSummary(color)).join(" · ")
                            : "Sin color")}`}
                    </Text>
                ))}
            </View>
        </Page>
        {sketchDataUrl ? (
            <Page size="A4" style={styles.page}>
                <View style={styles.header}>
                    <Image src={littleBlueLogo} style={styles.headerLogo} />
                    <Text style={styles.title}>BOCETO GENERADO</Text>
                    <Text style={styles.subtitle}>
                        {`${garmentName || "Tu prenda"} · ${generatedAt}`}
                    </Text>
                </View>
                <View style={styles.sketchWrap}>
                    <Image src={sketchDataUrl} style={styles.sketchImage} />
                </View>
            </Page>
        ) : null}
    </Document>
);

const blobToDataUrl = (blob) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(String(reader.result || ""));
    reader.onerror = reject;
    reader.readAsDataURL(blob);
});

const normalizeColorCode = (value) => {
    const raw = String(value || "").trim();
    if (!raw) return "";
    if (raw.startsWith("#")) return raw;
    if (/^[0-9a-fA-F]{3,8}$/.test(raw)) return `#${raw}`;
    return raw;
};

const resolvePrintTechniqueLabel = (selection = null) => {
    const attrs = selection?.attrs || {};
    const resolvedTechnique = String(attrs.resolved_technique || "").toLowerCase();
    if (resolvedTechnique === "aplique") return "Aplique";
    if (resolvedTechnique === "estampado" || resolvedTechnique === "serigrafia" || resolvedTechnique === "serigrafía") return "Estampado";
    return "Bordado";
};

const resolvePrintKind = (selection = null) => {
    const attrs = selection?.attrs || {};
    const mode = String(selection?.mode || "").toLowerCase();
    const printKind = String(attrs?.print_kind || "").toLowerCase();
    const textValue = String(attrs?.text || "").trim();
    if (mode === "text" || printKind === "text" || textValue) return "text";
    if (printKind === "predesign" || mode === "predesign") return "predesign";
    return "image_upload";
};

const resolvePrintKindLabel = (kind) => {
    if (kind === "text") return "Texto";
    if (kind === "predesign") return "Prediseño";
    return "Imagen personalizada";
};

const loadImageElement = (src) => new Promise((resolve, reject) => {
    if (typeof Image === "undefined") {
        reject(new Error("Image no disponible."));
        return;
    }
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = String(src || "");
});

const drawEmbroideryPredesignPreview = (ctx, image, w, h, fillColor, strokeColor) => {
    const fillCanvas = document.createElement("canvas");
    fillCanvas.width = w;
    fillCanvas.height = h;
    const fillCtx = fillCanvas.getContext("2d");
    if (!fillCtx) return;
    fillCtx.drawImage(image, 0, 0, w, h);
    fillCtx.globalCompositeOperation = "source-in";
    fillCtx.fillStyle = fillColor || "#ffffff";
    fillCtx.fillRect(0, 0, w, h);
    fillCtx.globalCompositeOperation = "source-over";

    const strokeCanvas = document.createElement("canvas");
    strokeCanvas.width = w;
    strokeCanvas.height = h;
    const strokeCtx = strokeCanvas.getContext("2d");
    if (!strokeCtx) return;

    const strokeSource = document.createElement("canvas");
    strokeSource.width = w;
    strokeSource.height = h;
    const strokeSourceCtx = strokeSource.getContext("2d");
    if (!strokeSourceCtx) return;
    strokeSourceCtx.drawImage(image, 0, 0, w, h);
    strokeSourceCtx.globalCompositeOperation = "source-in";
    strokeSourceCtx.fillStyle = strokeColor || fillColor || "#1f2a3d";
    strokeSourceCtx.fillRect(0, 0, w, h);
    strokeSourceCtx.globalCompositeOperation = "source-over";

    const thickness = Math.max(1, Math.round(Math.max(w, h) * 0.025));
    const offsets = [
        [-thickness, 0], [thickness, 0], [0, -thickness], [0, thickness],
        [-thickness, -thickness], [-thickness, thickness], [thickness, -thickness], [thickness, thickness],
    ];
    offsets.forEach(([ox, oy]) => strokeCtx.drawImage(strokeSource, ox, oy));
    strokeCtx.globalCompositeOperation = "destination-out";
    strokeCtx.drawImage(fillCanvas, 0, 0);
    strokeCtx.globalCompositeOperation = "source-over";

    ctx.clearRect(0, 0, w, h);
    ctx.drawImage(strokeCanvas, 0, 0);
    ctx.drawImage(fillCanvas, 0, 0);
};

const buildTintedPredesignDataUrl = async ({ sourceUrl, colorHexes = [], isEmbroidery = false }) => {
    if (typeof document === "undefined" || !sourceUrl) return sourceUrl;
    try {
        const image = await loadImageElement(sourceUrl);
        const w = image.naturalWidth || image.width || 1;
        const h = image.naturalHeight || image.height || 1;
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d");
        if (!ctx) return sourceUrl;
        ctx.clearRect(0, 0, w, h);
        ctx.drawImage(image, 0, 0, w, h);

        const validHexes = (colorHexes || []).filter(Boolean);
        if (!validHexes.length) return sourceUrl;

        if (isEmbroidery && validHexes.length > 1) {
            drawEmbroideryPredesignPreview(ctx, image, w, h, validHexes[0], validHexes[1]);
        } else {
            ctx.globalCompositeOperation = "source-in";
            ctx.fillStyle = validHexes[0] || "#ffffff";
            ctx.fillRect(0, 0, w, h);
            ctx.globalCompositeOperation = "source-over";
        }
        return canvas.toDataURL("image/png");
    } catch (error) {
        return sourceUrl;
    }
};

// react-pdf solo dibuja PNG/JPG. La imagen PROPIA del cliente (escudo/logo) puede venir en SVG o
// WEBP (muy común en escudos de colegio) → quedaba en blanco en el PDF. La pasamos por canvas a
// PNG para que SIEMPRE se vea (preserva transparencia). Si ya es PNG/JPG, la dejamos tal cual.
const normalizeUploadedImageToPng = async (sourceUrl) => {
    if (typeof document === "undefined" || !sourceUrl) return sourceUrl;
    const src = String(sourceUrl).trim();
    if (/^data:image\/(png|jpe?g);/i.test(src)) return src;
    try {
        const image = await loadImageElement(src);
        const w = image.naturalWidth || image.width || 1;
        const h = image.naturalHeight || image.height || 1;
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d");
        if (!ctx) return src;
        ctx.clearRect(0, 0, w, h);
        ctx.drawImage(image, 0, 0, w, h);
        return canvas.toDataURL("image/png");
    } catch (error) {
        return src;
    }
};

const loadedCanvasFonts = new Set();
const loadCanvasFont = async ({ family, file, baseUrl = MASTER_PRINT_FONT_BASE_URL }) => {
    if (typeof document === "undefined" || typeof FontFace === "undefined") return;
    if (!family || !file) return;
    if (loadedCanvasFonts.has(family)) return;
    try {
        // `file` puede ser una URL completa (file_url del ABM) o un file relativo contra baseUrl.
        const src = String(file).startsWith("http") ? String(file) : `${String(baseUrl || MASTER_PRINT_FONT_BASE_URL)}${String(file)}`;
        const font = new FontFace(String(family), `url(${src})`);
        await font.load();
        document.fonts?.add(font);
        loadedCanvasFonts.add(family);
    } catch (error) {
        // fallback silencioso a Helvetica/Arial
    }
};

const buildTextPreviewDataUrl = async ({
    text = "",
    fontFamily = "Arial",
    fontFile = null,
    fontBaseUrl = MASTER_PRINT_FONT_BASE_URL,
    colorHexes = [],
}) => {
    if (typeof document === "undefined") return null;
    const safeText = String(text || "").trim();
    if (!safeText) return null;

    await loadCanvasFont({ family: fontFamily, file: fontFile, baseUrl: fontBaseUrl });

    const canvas = document.createElement("canvas");
    canvas.width = 1100;
    canvas.height = 340;
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;

    const fillColor = normalizeColorCode(colorHexes?.[0] || "#1f2d4d");
    const strokeColor = normalizeColorCode(colorHexes?.[1] || "");
    const shadowColor = normalizeColorCode(colorHexes?.[2] || "");

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = fillColor || "#1f2d4d";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.font = `700 120px "${fontFamily || "Arial"}"`;

    if (shadowColor) {
        ctx.shadowColor = shadowColor;
        ctx.shadowBlur = 6;
        ctx.shadowOffsetX = 2;
        ctx.shadowOffsetY = 2;
    }

    if (strokeColor) {
        ctx.lineWidth = 5;
        ctx.strokeStyle = strokeColor;
        ctx.strokeText(safeText, canvas.width / 2, canvas.height / 2);
    }
    ctx.fillText(safeText, canvas.width / 2, canvas.height / 2);

    return canvas.toDataURL("image/png");
};

const resolveAppliedPrintColors = (selection = null, allColorsById = new Map()) => {
    const attrs = selection?.attrs || {};
    const slots = [1, 2, 3];
    const result = [];
    slots.forEach((slot) => {
        const attrKey = `color${slot}`;
        const rawId = slot === 1 ? (attrs[attrKey] ?? attrs.color ?? null) : (attrs[attrKey] ?? null);
        const id = String(rawId ?? "").trim();
        if (!id) return;
        const colorObj = allColorsById.get(id);
        const colorName = sanitizeColorName(colorObj?.name || `Color ${id}`, `Color ${id}`);
        const colorCode = normalizeColorCode(colorObj?.code || colorObj?.default_code || "");
        const label = `C${slot}: ${colorName}${colorCode ? ` (${colorCode})` : ""}`;
        result.push({
            slot,
            id,
            name: colorName,
            code: colorCode,
            label,
        });
    });
    return result;
};

const buildNodeByKeyMap = (root) => {
    const map = new Map();
    const walk = (node) => {
        if (!node) return;
        map.set(String(node.key), node);
        (node.children || []).forEach(walk);
    };
    walk(root);
    return map;
};

const resolvePartColorInfo = (row = null, allColorsById = new Map()) => {
    if (!row || row.master_print_id) return null;
    const directColorId = row?.color_id ?? row?.attrs?.color ?? null;
    const colorKey = String(directColorId || "").trim();
    if (!colorKey) return null;
    const colorObj = allColorsById.get(colorKey);
    if (!colorObj?.name) return null;
    return {
        label: sanitizeColorName(colorObj.name, ""),
        code: normalizeColorCode(colorObj.code || ""),
    };
};

const isSkippablePartType = (type) => {
    const normalized = Number(type || 0);
    return (
        normalized === Number(MASTER_PART_TYPE.PRINTS)
        || normalized === Number(MASTER_PART_TYPE.EXTRA)
    );
};

const buildSelectedPartsTreeRows = ({
    selectedTree = [],
    normalizedRoot = null,
    appliedPartsPreview = [],
    allColors = [],
}) => {
    if (!Array.isArray(selectedTree) || selectedTree.length === 0 || !normalizedRoot) return [];

    const allColorsById = new Map(
        (allColors || [])
            .map((color) => [String(color?.id || "").trim(), color])
            .filter(([key]) => key)
    );
    const partColorById = new Map();
    (appliedPartsPreview || []).forEach((row) => {
        const key = String(row?.master_part_id || "").trim();
        if (!key) return;
        if (partColorById.has(key)) return;
        const info = resolvePartColorInfo(row, allColorsById);
        if (info?.label) partColorById.set(key, info);
    });

    const nodeByKey = buildNodeByKeyMap(normalizedRoot);
    const selectedRoot = selectedTree[0];
    if (!selectedRoot) return [];

    const promoteIncludedChildren = (rawNode) => {
        if (!rawNode) return [];
        const key = String(rawNode.key || "");
        const sourceNode = nodeByKey.get(key) || null;
        const childrenBuilt = (rawNode.children || [])
            .flatMap((child) => promoteIncludedChildren(child));

        const isRoot = key === "master";
        const selected = !!rawNode.selected || isRoot;
        const skippable = !isRoot && isSkippablePartType(sourceNode?.type);
        if (!selected || skippable) {
            return childrenBuilt;
        }

        const label = isRoot
            ? "Master"
            : String(
                sourceNode?.Item?.nname
                || sourceNode?.Item?.name
                || sourceNode?.name
                || `Parte ${key}`
            ).trim();
        const type = Number(sourceNode?.type || 0);
        const colorInfo = !isRoot ? (partColorById.get(key) || null) : null;
        return [{
            key,
            label,
            type,
            colorLabel: String(colorInfo?.label || "").trim(),
            colorCode: String(colorInfo?.code || "").trim(),
            isPartType: type === Number(MASTER_PART_TYPE.PART_TYPE),
            children: childrenBuilt,
        }];
    };

    const includedRoots = promoteIncludedChildren(selectedRoot);
    const master = includedRoots.find((node) => String(node?.key) === "master") || includedRoots[0] || null;
    if (!master) return [];

    const rows = [];
    const walk = (nodes = [], ancestorHasNext = []) => {
        nodes.forEach((node, index) => {
            const isLast = index === nodes.length - 1;
            const safeLabel = String(node?.label || "Parte").trim() || "Parte";
            rows.push({
                key: String(node?.key || `${safeLabel}-${index}`),
                label: safeLabel,
                colorLabel: node?.colorLabel ? String(node.colorLabel) : "",
                colorCode: node?.colorCode ? String(node.colorCode) : "",
                isPartType: !!node?.isPartType,
                isLast,
                ancestorHasNext: [...ancestorHasNext],
            });
            walk(node?.children || [], [...ancestorHasNext, !isLast]);
        });
    };

    walk(master.children || []);
    return rows;
};

const buildPrintResources = async ({ selectedPrints = {}, allColors = [], fontBaseUrl = MASTER_PRINT_FONT_BASE_URL }) => {
    const allColorsById = new Map(
        (allColors || [])
            .map((color) => [String(color?.id || "").trim(), color])
            .filter(([key]) => key)
    );

    const entries = Object.entries(selectedPrints || {});
    const resources = await Promise.all(entries.map(async ([printKey, selection], index) => {
        if (!hasRealPrintContent(selection)) return null;
        const attrs = selection?.attrs || {};
        const kind = resolvePrintKind(selection);
        const kindLabel = resolvePrintKindLabel(kind);
        const techniqueLabel = resolvePrintTechniqueLabel(selection);
        const appliedColors = resolveAppliedPrintColors(selection, allColorsById);
        const appliedColorsLabel = appliedColors.map((color) => color.label).join(" · ");
        const colorHexes = appliedColors.map((color) => color.code).filter(Boolean);
        const normalizedSelection = isFixedPredesignSelection(selection)
            ? {
                ...selection,
                attrs: applyPredesignSizeAttrs(selection),
            }
            : selection;
        const sizeLabel = isFixedPredesignSelection(selection)
            ? getSelectionSizeLabel(normalizedSelection)
            : formatPrintSizeCm(getSelectionEffectiveSizeCm(normalizedSelection), { roundUpHalf: true });

        if (kind === "text") {
            const textValue = String(attrs?.text || "").trim();
            if (!textValue) return null;
            const fontId = Number(attrs?.font || 0);
            const fontMeta = fontId ? getFontById(fontId) : null;
            const fontFamily = fontMeta?.family || "Arial";
            const fontFile = getFontFaceUrl(fontMeta);
            const previewUrl = await buildTextPreviewDataUrl({
                text: textValue,
                fontFamily,
                fontFile,
                fontBaseUrl,
                colorHexes,
            });
            return {
                id: String(printKey || `print-${index}`),
                kind,
                kindLabel,
                techniqueLabel,
                previewUrl,
                textValue,
                fontLabel: fontMeta?.name || fontFamily || "Arial",
                appliedColorsLabel,
                sizeLabel,
            };
        }

        const previewUrl = resolvePrintPreviewUrl(selection);
        if (!previewUrl) return null;
        const isEmbroidery = techniqueLabel.toLowerCase() === "bordado";
        const resolvedPreviewUrl = kind === "predesign"
            ? await buildTintedPredesignDataUrl({
                sourceUrl: previewUrl,
                colorHexes,
                isEmbroidery,
            })
            // Imagen propia del cliente (escudo/logo): normalizar a PNG para que se vea en el PDF.
            : await normalizeUploadedImageToPng(previewUrl);

        return {
            id: String(printKey || `print-${index}`),
            kind,
            kindLabel,
            techniqueLabel,
            previewUrl: resolvedPreviewUrl,
            textValue: "",
            fontLabel: "",
            appliedColorsLabel,
            sizeLabel,
        };
    }));

    return resources.filter(Boolean);
};

export const buildDesignerGuidePdfDataUrl = async ({
    orderItemId,
    garmentName = "Tu prenda",
    appliedPartsBySection = [],
    selectedTree = [],
    normalizedRoot = null,
    appliedPartsPreview = [],
    selectedPrints = {},
    allColors = [],
    fontBaseUrl = MASTER_PRINT_FONT_BASE_URL,
    stripeStrokeSummary = null,
    customCutSummary = null,
    sketchDataUrl = null,
}) => {
    const generatedAt = new Date().toLocaleString("es-AR");
    const appliedPartsTreeRows = buildSelectedPartsTreeRows({
        selectedTree,
        normalizedRoot,
        appliedPartsPreview,
        allColors,
    });
    const printResources = await buildPrintResources({ selectedPrints, allColors, fontBaseUrl });

    const document = (
        <GuideDocument
            orderItemId={orderItemId}
            garmentName={garmentName}
            generatedAt={generatedAt}
            appliedPartsBySection={appliedPartsBySection}
            appliedPartsTreeRows={appliedPartsTreeRows}
            printResources={printResources}
            stripeStrokeSummary={stripeStrokeSummary}
            customCutSummary={customCutSummary}
            sketchDataUrl={sketchDataUrl}
        />
    );
    const blob = await pdf(document).toBlob();
    return blobToDataUrl(blob);
};
